import React, { useCallback, useMemo, useState } from 'react';
import { Icon, StatusChip } from '../components/sp-primitives';
import { ConfirmModal } from '../components/sp-overlays';
import { PShell, SpWorkspaceLoading, useApp } from '../components/sp-shell';

export function PTemplates() {
  const app = useApp();
  const [confirmModal, setConfirmModal] = useState(null);
  const isAdminAccount = Array.isArray(app.currentUser?.permissions) && app.currentUser.permissions.includes('Administrador');
  const tpls = useMemo(() => {
    const list = Array.isArray(app.templates) ? [...app.templates] : [];
    return list.sort((a, b) => {
      const aInactive = a?.ativo === false ? 1 : 0;
      const bInactive = b?.ativo === false ? 1 : 0;
      return aInactive - bInactive;
    });
  }, [app.templates]);
  const [expandedById, setExpandedById] = useState({});
  const shipmentCountByTemplate = useMemo(() => {
    const batches = Array.isArray(app.batches) ? app.batches : [];
    const counts = new Map();
    batches.forEach((b) => {
      const status = String(b?.status || '');
      if (status !== 'approval' && status !== 'sent') return;
      const templateName = String(b?.template || '').trim().toLowerCase();
      if (!templateName) return;
      counts.set(templateName, (counts.get(templateName) || 0) + 1);
    });
    return counts;
  }, [app.batches]);

  const handleDeleteTemplate = useCallback(async (tpl) => {
    const id = tpl?.id != null ? String(tpl.id).trim() : '';
    if (!id || id === 'undefined' || !/^\d+$/.test(id)) {
      app.toast('error', 'Remover modelo', 'Identificador do modelo inválido. Abra o cadastro, salve novamente e tente excluir.');
      return;
    }
    setConfirmModal({
      title: 'Remover modelo',
      message: `Remover o modelo "${tpl?.n || ''}"? Esta ação não poderá ser desfeita.`,
      confirmLabel: 'Remover',
      onConfirm: async () => {
        try {
          await app.deleteTemplate(id);
          app.toast('success', 'Modelo removido', `"${tpl.n}" foi removido com sucesso.`);
        } catch (e) {
          if (e?.status === 422 && e?.body?.code === 'template_in_use') {
            app.toast('info', 'Exclusão não permitida', e.body.message
              || 'Este modelo já é utilizado por alguns pagamentos, você não pode excluí-lo, apenas desativá-lo.');
            return;
          }
          app.toast('error', 'Remover modelo', e?.message || 'Falha ao remover modelo.');
        }
      },
    });
  }, [app]);

  if (!app.workspaceReady) {
    return <SpWorkspaceLoading active="templates" title="A carregar modelos…" />;
  }

  const getShipmentCount = (tpl) => {
    const key = String(tpl?.n || '').trim().toLowerCase();
    return shipmentCountByTemplate.get(key) || 0;
  };
  const isExpanded = (tpl, idx) => !!expandedById[tpl?.id || idx];
  const toggleExpanded = (tpl, idx) => {
    const k = tpl?.id || idx;
    setExpandedById((prev) => ({ ...prev, [k]: !prev[k] }));
  };
  return (
    <PShell active="templates">
      <div className="sp-screen-head">
        <div>
          <div className="sp-caps sp-screen-head__caps">Modelos de importação</div>
          <h1 className="sp-screen-head__title">Layouts de CSV</h1>
        </div>
        {isAdminAccount && (
          <button type="button" className="sp-btn sp-btn-accent" onClick={() => app.navigate('template-new')}><Icon name="plus" size={13} /> Criar novo modelo</button>
        )}
      </div>
      <div className="sp-tpl-grid">
        {tpls.map((t, i) => (
          <div key={i} className={`sp-card sp-tpl-card${t.ativo ? '' : ' is-inactive'}`}>
            <div className="sp-tpl-head">
              <div className="sp-tpl-head__left">
                <div className="sp-tpl-head__icons">
                  <div className="sp-tpl-icon">
                    <Icon name={t.paymentType === 'individual' ? 'user' : 'users'} size={18} />
                  </div>
                  {t.paymentType === 'mass' && (
                    <div className="sp-tpl-icon sp-tpl-icon--format" title={`Formato ${t.fileType || 'CSV'}`}>
                      <Icon name={String(t.fileType || 'CSV').toUpperCase() === 'TXT' ? 'file' : 'table'} size={14} />
                      <span className="sp-tpl-icon__label">{String(t.fileType || 'CSV').toUpperCase()}</span>
                    </div>
                  )}
                  {t.paymentType === 'individual' && (
                    <div className="sp-tpl-icon sp-tpl-icon--format" title="Formato FORM">
                      <Icon name="edit" size={14} />
                      <span className="sp-tpl-icon__label">FORM</span>
                    </div>
                  )}
                </div>
                <div>
                  <div className="sp-tpl-name">{t.n}</div>
                </div>
              </div>
              <div className="sp-tpl-toolbar">
                <button type="button" className="sp-btn sp-btn-ghost sp-btn-compact" onClick={() => app.navigate('template-new', { editing: t })}><Icon name="edit" size={13} /></button>
                <button type="button" className="sp-btn sp-btn-ghost sp-btn-compact" onClick={() => handleDeleteTemplate(t)}><Icon name="trash" size={13} /></button>
              </div>
            </div>
            <div className="sp-tpl-meta">
              <div><span className="sp-caps sp-tpl-meta__caps">Tipo de pagamento</span><span className="sp-tpl-meta__val">{t.paymentType === 'individual' ? 'Individual' : 'Em massa'}</span></div>
              <div>
                <span className="sp-caps sp-tpl-meta__caps">Formato</span>
                <span className="sp-tpl-meta__val">{t.paymentType === 'individual' ? 'Formulário' : (t.fileType || 'CSV')}</span>
              </div>
              {t.paymentType !== 'individual' && (
                <div><span className="sp-caps sp-tpl-meta__caps">Delimitador</span><span className="sp-mono sp-tpl-meta__mono">{t.sep === ';' ? 'ponto-vírgula (;)' : 'vírgula (,)'}</span></div>
              )}
              {isExpanded(t, i) && (
                <>
                  <div><span className="sp-caps sp-tpl-meta__caps">Identificação no Sienge</span><span className="sp-tpl-meta__val">{t.strat === 'BY_CNPJ' ? 'Por CNPJ' : 'Por CPF'}</span></div>
                  {t.paymentType !== 'individual' && (
                    <div><span className="sp-caps sp-tpl-meta__caps">Localização do identificador</span><span className="sp-tpl-meta__val">{t.identifierFieldMode === 'COLUMN_POSITION' ? 'Posição da Coluna' : 'Nome da Coluna'}</span></div>
                  )}
                  {t.paymentType !== 'individual' && (
                    <div>
                      <span className="sp-caps sp-tpl-meta__caps">
                        {t.identifierFieldMode === 'COLUMN_POSITION'
                          ? 'Posição da Coluna com Identificador'
                          : 'Título na 1º linha'}
                      </span>
                      <span className="sp-tpl-meta__val">{t.identifierFieldLocation || '—'}</span>
                    </div>
                  )}
                  <div><span className="sp-caps sp-tpl-meta__caps">Total de Envios</span><span className="sp-tpl-meta__val">{getShipmentCount(t)}</span></div>
                </>
              )}
              <div>
                <span className="sp-caps sp-tpl-meta__caps">Status</span>
                {t.ativo ? <StatusChip kind="green">ativo</StatusChip> : <StatusChip kind="red">inativo</StatusChip>}
              </div>
            </div>
            <div style={{ marginTop: 8 }}>
              <button type="button" className="sp-btn sp-btn-ghost sp-btn-compact" onClick={() => toggleExpanded(t, i)}>
                {isExpanded(t, i) ? 'Recolher detalhes' : 'Expandir detalhes'}
              </button>
            </div>
          </div>
        ))}
        {tpls.length === 0 && (
          <div className="sp-card sp-empty-hint-card">
            Nenhum modelo cadastrado.
          </div>
        )}
      </div>
      <ConfirmModal
        open={!!confirmModal}
        title={confirmModal?.title}
        message={confirmModal?.message}
        confirmLabel={confirmModal?.confirmLabel}
        onConfirm={() => {
          confirmModal?.onConfirm?.();
          setConfirmModal(null);
        }}
        onCancel={() => setConfirmModal(null)}
      />
    </PShell>
  );
}
